import { msalInstance } from './entraAuth';

// API request and response types
export interface LambdaPayload {
  action: 'getAllSolutions' | 'getSolution' | 'getAllBlogs' | 'getBlog' | 'getAllAgents';
  payload?: {
    eventId?: string;
    [key: string]: any;
  };
  username?: string;
}

export interface LambdaResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// API Gateway endpoint. The trailing slash is required - requests without it 404.
const DEFAULT_API_BASE_URL =
  'https://qxnw356px9.execute-api.us-east-1.amazonaws.com/production/';

const getApiBaseUrl = (): string =>
  process.env.REACT_APP_API_BASE_URL || DEFAULT_API_BASE_URL;

// Abort a hanging request rather than leaving it open indefinitely
const REQUEST_TIMEOUT_MS = 30000;

// Extract username from Entra token
const getUsernameFromToken = (): string | undefined => {
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      // Try to get username from account info
      // MSAL provides username in the account object
      const account = accounts[0];
      return account.username || account.name;
    }
  } catch (error) {
    console.warn('Failed to extract username from Entra token:', error);
  }
  return undefined;
};

/**
 * Performs the HTTP POST against API Gateway.
 * Throws on network failures, timeouts, non-2xx responses, and unparseable
 * bodies so that callers can decide whether to retry.
 */
async function postAction<T>(payload: LambdaPayload): Promise<LambdaResponse<T>> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(getApiBaseUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: payload.action,
        username: getUsernameFromToken(),
        ...(payload.payload === undefined ? {} : { payload: payload.payload })
      }),
      signal: controller.signal
    });
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(`Request timed out after ${REQUEST_TIMEOUT_MS}ms`);
    }
    throw new Error(
      error instanceof Error ? `Network error: ${error.message}` : 'Network error'
    );
  } finally {
    clearTimeout(timeoutId);
  }

  const rawBody = await response.text();

  if (!response.ok) {
    throw new Error(
      `API request failed with status ${response.status} ${response.statusText}: ${
        rawBody || '(empty response body)'
      }`
    );
  }

  try {
    return JSON.parse(rawBody) as LambdaResponse<T>;
  } catch {
    throw new Error('Failed to parse API response as JSON');
  }
}

// Generic API invocation function
export async function invokeLambda<T>(payload: LambdaPayload): Promise<LambdaResponse<T>> {
  try {
    return await postAction<T>(payload);
  } catch (error) {
    console.error('API invocation error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

// Retry logic with exponential backoff
export async function invokeLambdaWithRetry<T>(
  payload: LambdaPayload,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<LambdaResponse<T>> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // The API responded, so return its envelope as-is - including
      // { success: false } business errors, which are not retryable.
      return await postAction<T>(payload);
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');
      console.error(`API invocation error (attempt ${attempt + 1}):`, lastError);

      // Don't wait after the last attempt
      if (attempt < maxRetries - 1) {
        const delay = initialDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  return {
    success: false,
    error: lastError?.message || 'Failed after multiple retries'
  };
}
